import { MutableRefObject, useContext } from "react";
import { Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Button, Col, Form, Input, Row, Select, Space } from "antd";
import {
    ArrowLeftOutlined,
    ArrowRightOutlined,
    CalendarOutlined,
    EditOutlined,
    InfoCircleOutlined,
    SaveOutlined,
} from "@ant-design/icons";

import { IGiveawayAuto } from "../../../../../../interfaces";
import { ThemeContext } from "../../../../../../contexts/ThemeContext";
import DatePicker from "../../../../../../components/DatePicker";
import useGiveawayDataStep from "./useGiveawayDataStep";
import { DateFns } from "../../../../../../utils/dateFns";

interface IProps {
    giveawayAuto?: IGiveawayAuto;
    onSubmit: (user: IGiveawayAuto) => Promise<void>;
    onSubmitGiveawayDataStepFinished: () => void;
    refIsEditing: MutableRefObject<boolean>;
    canEdit: boolean;
}

export default function GiveawayDataStep({
    onSubmit,
    giveawayAuto,
    onSubmitGiveawayDataStepFinished,
    refIsEditing,
    canEdit,
}: IProps) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';
    const navigate = useNavigate();

    const {
        isSubmiting,
        handleSubmit,
        handleSubmitForm,
        control,
        errors,
        handleChangeDateRange,
        handleClickUpdateAndLeave,
        watch,
        setValue,
    } = useGiveawayDataStep({ onSubmit, giveawayAuto, onSubmitGiveawayDataStepFinished });

    const sectionTitleStyle: React.CSSProperties = {
        fontSize: 13,
        fontWeight: 600,
        color: isDark ? '#94a3b8' : '#64748b',
        textTransform: 'uppercase',
        letterSpacing: '0.4px',
        marginBottom: 12,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
    };

    return (
        <Form
            onFinish={handleSubmit(handleSubmitForm)}
            autoComplete="off"
            layout="vertical"
        >
            <div
                style={{
                    background: isDark ? '#141414' : '#ffffff',
                    borderRadius: 10,
                    border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                    boxShadow: isDark
                        ? '0 4px 20px rgba(0, 0, 0, 0.35)'
                        : '0 2px 10px rgba(0, 0, 0, 0.03)',
                    padding: '24px',
                }}
            >
                {/* Seção 1: Identificação & Regras */}
                <div style={{ marginBottom: 20 }}>
                    <div style={sectionTitleStyle}>
                        <InfoCircleOutlined style={{ color: '#1677ff' }} />
                        <span>Identificação & Tipo de Apuração</span>
                    </div>

                    <Row gutter={[12, 12]}>
                        <Col xs={24} md={16} lg={16}>
                            <Form.Item
                                label="Descrição do Sorteio"
                                style={{ marginBottom: 0 }}
                                validateStatus={errors.description ? 'error' : undefined}
                                help={errors.description?.message}
                            >
                                <Controller
                                    name="description"
                                    control={control}
                                    rules={{ required: { value: true, message: 'Por favor, digite a descrição do sorteio!' } }}
                                    render={({ field }) => (
                                        <Input
                                            {...field}
                                            prefix={<EditOutlined style={{ color: isDark ? '#666' : '#bfbfbf' }} />}
                                            placeholder="Ex: Sorteio Mensal - Importação Automática"
                                            disabled={refIsEditing.current && !canEdit}
                                        />
                                    )}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24} md={8} lg={8}>
                            <Form.Item
                                label="Tipo de Apuração"
                                style={{ marginBottom: 0 }}
                                validateStatus={errors.type ? 'error' : undefined}
                                help={errors.type?.message}
                                tooltip="Identifica como será feita a apuração do sorteio."
                            >
                                <Controller
                                    name="type"
                                    control={control}
                                    rules={{ required: { value: true, message: 'Por favor, selecione o tipo do sorteio!' } }}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            placeholder="Selecione o tipo"
                                            defaultValue="Loteria Federal"
                                            style={{ width: '100%' }}
                                            onChange={(value) => {
                                                field.onChange(value);
                                                setValue('date', '');
                                            }}
                                            options={[
                                                { value: 'Loteria Federal', label: 'Loteria Federal' },
                                                { value: 'Interno', label: 'Interno (Sistema)' },
                                            ]}
                                            disabled={refIsEditing.current && !canEdit}
                                        />
                                    )}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                </div>

                {/* Seção 2: Período & Cronograma */}
                <div style={{ marginBottom: 20 }}>
                    <div style={sectionTitleStyle}>
                        <CalendarOutlined style={{ color: '#722ed1' }} />
                        <span>Período & Data do Sorteio</span>
                    </div>

                    <Row gutter={[12, 12]}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="Período de Participação"
                                style={{ marginBottom: 0 }}
                                validateStatus={errors.period_beginning || errors.period_ending ? 'error' : undefined}
                                help={errors.period_beginning?.message || errors.period_ending?.message}
                                tooltip="Intervalo de datas das notas fiscais válidas para este sorteio."
                            >
                                <Controller
                                    name="period_beginning"
                                    control={control}
                                    rules={{
                                        required: {
                                            value: true,
                                            message: "Por favor, informe o período do sorteio!"
                                        },
                                    }}
                                    render={({ field: beginningField }) => (
                                        <Controller
                                            name="period_ending"
                                            control={control}
                                            render={({ field: endingField }) => (
                                                <DatePicker.RangePicker
                                                    format={(date) => date.toLocaleDateString('PT-BR')}
                                                    value={[
                                                        beginningField.value ? new Date(beginningField.value) : null,
                                                        endingField.value ? new Date(endingField.value) : null
                                                    ]}
                                                    onChange={handleChangeDateRange}
                                                    style={{ width: '100%' }}
                                                    disabled={refIsEditing.current && !canEdit}
                                                    allowClear
                                                />
                                            )}
                                        />
                                    )}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24} md={12}>
                            <Form.Item
                                label="Data do Sorteio"
                                style={{ marginBottom: 0 }}
                                validateStatus={errors.date ? 'error' : undefined}
                                help={errors.date?.message}
                            >
                                <Controller
                                    name="date"
                                    control={control}
                                    rules={{
                                        required: {
                                            value: true,
                                            message: "Por favor, informe a data do sorteio!"
                                        }
                                    }}
                                    render={({ field }) => (
                                        <DatePicker
                                            format={(date) => date.toLocaleDateString('PT-BR', { hour: '2-digit', minute: '2-digit' })}
                                            value={field.value ? new Date(field.value) : undefined}
                                            onChange={(date) => {
                                                if (date && watch('type') === 'Loteria Federal') {
                                                    date = DateFns.setHour(date, 19);
                                                }
                                                field.onChange(date);
                                            }}
                                            style={{ width: '100%' }}
                                            disabled={refIsEditing.current && !canEdit}
                                            disabledDate={(currentDate) => {
                                                if (!currentDate) return false;

                                                const selectedDate = new Date(currentDate);
                                                selectedDate.setHours(0, 0, 0, 0);

                                                if (watch("type") !== "Interno") {
                                                    const day = currentDate.getDay();
                                                    return day !== 3 && day !== 6;
                                                }

                                                return false;
                                            }}
                                            disabledTime={(currentDate) => {
                                                return {};
                                            }}
                                            showTime={watch('type') !== 'Loteria Federal' && ({
                                                minuteStep: 30,
                                                format: 'HH:mm',
                                                showSecond: false,
                                                defaultValue: DateFns.setTimeToSixPM(new Date()),
                                            })}
                                        />
                                    )}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                </div>

                {/* Barra de Ações Inferior */}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 12,
                        paddingTop: 16,
                        borderTop: `1px solid ${isDark ? '#262626' : '#f0f2f5'}`,
                    }}
                >
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate('/sorteios/auto')}
                        style={{
                            borderRadius: 8,
                            height: 38,
                            padding: '0 16px',
                            fontWeight: 500,
                        }}
                    >
                        Voltar para Sorteios
                    </Button>

                    <Space size={10}>
                        {giveawayAuto && (
                            <Button
                                type="default"
                                htmlType="button"
                                icon={<SaveOutlined />}
                                disabled={isSubmiting || !canEdit}
                                onClick={handleSubmit(handleClickUpdateAndLeave)}
                                style={{
                                    height: 38,
                                    borderRadius: 8,
                                    padding: '0 16px',
                                    fontWeight: 500,
                                }}
                            >
                                Salvar e Sair
                            </Button>
                        )}

                        <Button
                            type="primary"
                            htmlType="submit"
                            icon={<ArrowRightOutlined />}
                            loading={isSubmiting}
                            disabled={isSubmiting || (giveawayAuto && !canEdit)}
                            style={{
                                height: 38,
                                borderRadius: 8,
                                fontWeight: 600,
                                padding: '0 20px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                            }}
                        >
                            Avançar para Prêmios
                        </Button>
                    </Space>
                </div>
            </div>
        </Form>
    );
}
